import {
  checkDocuments,
  createDocument,
  getDocuments,
  updateDocument,
} from '../services/documents';
import doRequest from '../services/request';
import { UserDashboardDetailResponse, UsersDashboardProps } from '../types/responses/UsersDashboard';
import { UsersDataProps } from '../types/responses/usersDetailsResponse';
import { UsersPrivateStationProps } from '../types/responses/UsersPrivateStation';

const getUsersDetails = async (): Promise<UsersDataProps> => {
  const data: any = await getDocuments('users_data/details');
  return data;
};

const getUsers = async (): Promise<any> => {
  const data = await getDocuments('users_data');
  return data;
};

const getUserDetail = async (userPk: any) => {
  const data = await getDocuments(`users_data/${userPk}`);
  return data;
};

const getTrustedFlagAudit = async (userPk: number) => {
  const data = await getDocuments(`users_data/${userPk}/trusted-flag-audit`);
  return Array.isArray(data) ? data : [];
};

const updateUserDetail = async (userDetail: any, userPk: any) => {
  const data = await updateDocument(userPk, userDetail, 'users_data');
  return data;
};

const getUserAddress = async (userPk: any) => {
  const data = await getDocuments(`users_data/${userPk}/address`);
  return data;
};

const updateUserAddress = async (userAddress: any, userPk: any) => {
  const data = await updateDocument(
    `${userPk}/address`,
    userAddress,
    'users_data',
  );
  return data;
};

const createUser = async (user: any) => {
  const data = await createDocument(user, 'users_data');
  return data;
};

const checkUser = async () => {
  const data = await checkDocuments('users_data');
  return data;
};

const getUserGroups = async (userPk = null) => {
  const data = await getDocuments(
    `user-groups${userPk != null ? '?userPk=' + userPk : ''}`,
  );
  return data;
};

const getUsersDashboard = async (
  activeUsers = true,
  page = 1,
  size = 10,
  searchInput: string = '',
  isVehicle: boolean = false,
): Promise<UsersDashboardProps> => {
  let usersUrl: string = `users_data/dashboard-details?activeUsers=${activeUsers}&page=${page}&size=${size}&isVehicle=${isVehicle}`;

  if (searchInput) {
    usersUrl = usersUrl.concat(`&searchInput=${searchInput}`);
  }

  const data: any = await getDocuments(usersUrl);

  return data;
};

const getUserDashboardDetail = async (
  userPk: number,
): Promise<UserDashboardDetailResponse> => {
  const data: any = await getDocuments(`users_data/dashboard-details/${userPk}`);
  return data;
};

const getDocTypes = async () => {
  const data: any = await getDocuments('users_data/doc_types');
  return data;
};

const getUserPrivateStation = async (
  chargeBoxPK,
): Promise<UsersPrivateStationProps> => {
  const data: any = await getDocuments(
    `users_data/private_charge_point${
      chargeBoxPK != null ? '?chargeBoxPK=' + chargeBoxPK : ''
    }`,
  );
  return data;
};

const searchUsers = async (
  chargeBoxPK: number | null,
  search: string,
): Promise<UsersPrivateStationProps> => {
  const data: any = await getDocuments(
    `users_data/private_charge_point?search=${encodeURIComponent(search)}${
      chargeBoxPK != null ? '&chargeBoxPK=' + chargeBoxPK : ''
    }`,
  );
  return data;
};

const getTenantParentUserByCPF = async (
  chargeBoxPK,
  cpf,
): Promise<UsersPrivateStationProps> => {
  const data: any = await getDocuments(
    `users_data/tenant_parent/cpf/${cpf}${
      chargeBoxPK != null ? '?chargeBoxPK=' + chargeBoxPK : ''
    }`,
  );
  return data;
};

const getTenantParentUserByCNPJ = async (
  chargeBoxPK,
  cnpj,
): Promise<UsersPrivateStationProps> => {
  const data: any = await getDocuments(
    `users_data/tenant_parent/cnpj/${cnpj}${
      chargeBoxPK != null ? '?chargeBoxPK=' + chargeBoxPK : ''
    }`,
  );
  return data;
};

const getUserLocations = async (level: 'state' | 'city' | 'country', state?: string) => {
  let url = `users_data/locations?level=${level}`;
  if (state) url = url.concat(`&state=${encodeURIComponent(state)}`);
  const data: any = await getDocuments(url);
  return data;
};

const getDeposits = async (userId) => {
  const data: any = await getDocuments(
    `customer-wallet/userDeposits/${userId}`,
  );

  return data;
};

const getDepositsTotalizers = async (userId) => {
  const data: any = await getDocuments(
    `customer-wallet/userDeposits/totalizer/${userId}`,
  );

  return data;
};

const manualWalletAdjustment = async (
  userId: number,
  payload: { amount: number; operation: 'ADD' | 'REMOVE' },
) => {
  const data: any = await createDocument(
    payload,
    `customer-wallet/userDeposits/${userId}/manual-adjustment`,
  );
  return data;
};

const getUserDashTransactionPermission = async () => {
  const method = 'GET';
  const data = await doRequest('report/user/permissions', { method });

  return data;
};

export {
  getUsersDetails,
  getUsers,
  getUserDetail,
  getTrustedFlagAudit,
  updateUserDetail,
  getUserAddress,
  updateUserAddress,
  createUser,
  getUserGroups,
  getUsersDashboard,
  getUserDashboardDetail,
  getDocTypes,
  getUserPrivateStation,
  searchUsers,
  getTenantParentUserByCPF,
  getTenantParentUserByCNPJ,
  getUserLocations,
  getDeposits,
  getDepositsTotalizers,
  manualWalletAdjustment,
  checkUser,
  getUserDashTransactionPermission,
};
